import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { LogOut, User, KeyRound } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarTrigger } from "@/components/ui/sidebar"
import GlobalSearch from "@/components/GlobalSearch"
import ChangePasswordModal from "@/components/ChangePasswordModal"

export function AppHeader() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const location = useLocation()
  const [showChangePassword, setShowChangePassword] = useState(false)

  const pathname = location.pathname
  const pageTitle = (() => {
    if (pathname === "/") return "📊 Dashboard"
    if (pathname === "/new-scan") return "🆕 Start New Scan"
    if (pathname.startsWith("/scan/")) return "📄 Scan Results"
    if (pathname === "/history") return "🗂️ Scan History"
    if (pathname === "/review-queue") return "📝 Review Queue"
    if (pathname === "/monitoring") return "📊 Monitored Content"
    if (pathname === "/scheduled-scans") return "⏰ Scheduled Scans"
    if (pathname === "/visibility-trends") return "📈 Search Visibility"
    if (pathname === "/domain-analytics") return "🎯 Domain Analytics & Repeat Offenders"
    if (pathname === "/api-monitor") return "📊 API Monitor"
    if (pathname === "/user-management") return "👥 User Management"
    if (pathname === "/settings") return "⚙️ Settings"
    return "OSN+ Piracy Detection"
  })()

  const pageSubtitle = (() => {
    if (pathname === "/") return "Overview of your piracy detection system"
    if (pathname === "/new-scan") return "Enter content to scan for piracy violations"
    if (pathname.startsWith("/scan/")) return "Detailed results and violation findings"
    if (pathname === "/history") return "Recent scan sessions and outcomes"
    if (pathname === "/review-queue") return "High‑confidence violations ready for takedown"
    if (pathname === "/monitoring") return "Titles under monitoring with quick actions"
    if (pathname === "/scheduled-scans") return "Automated scanning based on monitoring schedule"
    if (pathname === "/visibility-trends") return "Track search visibility over time"
    if (pathname === "/domain-analytics") return "Track pirate domains and repeat offenders"
    if (pathname === "/api-monitor") return "Realtime API health and performance"
    if (pathname === "/user-management") return "Manage user accounts and permissions"
    if (pathname === "/settings") return "Configure system options and scoring"
    return undefined
  })()

  const handleLogout = async () => {
    await logout()
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center gap-4 px-6 py-3 min-h-16">
        <SidebarTrigger />

        <div className="flex-1 min-w-0">
          <h1 className="truncate text-xl font-semibold">{pageTitle}</h1>
          {pageSubtitle && (
            <p className="truncate text-sm text-muted-foreground">{pageSubtitle}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <GlobalSearch
            onGotoHistory={(q) => navigate(`/history?q=${encodeURIComponent(q)}`)}
            onGotoDomainAnalytics={() => navigate("/domain-analytics")}
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
                <KeyRound className="mr-2 h-4 w-4" />
                <span>Change Password</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </header>
  )
}
